// useWorkflowRunStream-reducer — Reducer, initial state, and frame dispatcher
// for the workflow run stream hook.
//
// Extracted from useWorkflowRunStream.ts to keep each file under 200 LOC.
// Only exports what the hook file needs — StreamAction, INITIAL_STATE, reducer,
// makeRunEvent, dispatchFrameToReducer.
import type { RunStatus, StepStatus, RunEvent, Checkpoint } from "@/types/workflow.types";
import type { Frame } from "@/lib/realtime/types";
import type { StepStreamState, WorkflowRunStreamState } from "./useWorkflowRunStream";

// ── Internal action union ─────────────────────────────────────────────────────
// Not exported — consumers never dispatch actions directly.

export type StreamAction =
  | { type: "connected" }
  | { type: "disconnected" }
  | { type: "terminated" }
  | { type: "fallback_polling" }
  | { type: "event"; event: RunEvent }
  | { type: "token_delta"; stepId: string; delta: string }
  | { type: "step_text_complete"; stepId: string; text: string; event: RunEvent }
  | { type: "decision_made"; stepId: string; label: string; reasoning: string; chosenBranchIds: string[]; event: RunEvent }
  | { type: "step_update"; stepId: string; patch: Partial<StepStreamState> }
  | { type: "checkpoint_update"; checkpoint: Checkpoint | null }
  | { type: "run_status"; status: RunStatus; paused?: boolean; pausedReason?: string }
  | { type: "error"; message: string }
  | { type: "reset" };

// ── Initial state ─────────────────────────────────────────────────────────────

export const INITIAL_STATE: WorkflowRunStreamState = {
  isConnected: false,
  runStatus: null,
  paused: false,
  checkpointPending: null,
  steps: {},
  eventsByStepId: {},
  allEvents: [],
  liveTokensByStepId: {},
  lastEventId: null,
  useFallbackPolling: false,
  terminated: false,
};

// ── Reducer ───────────────────────────────────────────────────────────────────

export function reducer(
  state: WorkflowRunStreamState,
  action: StreamAction,
): WorkflowRunStreamState {
  switch (action.type) {
    case "connected":
      return { ...state, isConnected: true, error: undefined };
    case "disconnected":
      return { ...state, isConnected: false };
    case "terminated":
      return { ...state, isConnected: false, terminated: true };
    case "fallback_polling":
      return { ...state, useFallbackPolling: true, isConnected: false };
    case "run_status": {
      const nextPaused = action.paused ?? state.paused;
      return {
        ...state,
        runStatus: action.status,
        paused: nextPaused,
        // Clear stale pausedReason when transitioning back to not-paused.
        pausedReason: nextPaused ? (action.pausedReason ?? state.pausedReason) : undefined,
      };
    }
    case "event": {
      const ev = action.event;
      const stepId = ev.stepId ?? "__run__";
      const existing = state.eventsByStepId[stepId] ?? [];
      return {
        ...state,
        allEvents: [...state.allEvents, ev],
        eventsByStepId: {
          ...state.eventsByStepId,
          [stepId]: [...existing, ev],
        },
        lastEventId: ev.id || state.lastEventId,
      };
    }
    case "token_delta": {
      const prev = state.liveTokensByStepId[action.stepId] ?? "";
      return {
        ...state,
        liveTokensByStepId: {
          ...state.liveTokensByStepId,
          [action.stepId]: prev + action.delta,
        },
      };
    }
    case "step_text_complete": {
      const { [action.stepId]: _cleared, ...remaining } = state.liveTokensByStepId;
      return {
        ...state,
        liveTokensByStepId: remaining,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...(state.steps[action.stepId] ?? { stepId: action.stepId, status: "success" as StepStatus, progressPercent: 100 }),
            outputText: action.text,
          },
        },
        allEvents: [...state.allEvents, action.event],
        lastEventId: action.event.id || state.lastEventId,
      };
    }
    case "decision_made": {
      const updatedSteps = { ...state.steps };
      for (const [sid, st] of Object.entries(updatedSteps)) {
        if (
          !action.chosenBranchIds.includes(sid) &&
          (st.status === "pending" || st.status === "waiting")
        ) {
          updatedSteps[sid] = { ...st, status: "skipped_by_decision" };
        }
      }
      updatedSteps[action.stepId] = {
        ...(updatedSteps[action.stepId] ?? { stepId: action.stepId, status: "success" as StepStatus, progressPercent: 100 }),
        status: "success",
        decisionLabel: action.label,
        decisionReasoning: action.reasoning,
      };
      return {
        ...state,
        steps: updatedSteps,
        allEvents: [...state.allEvents, action.event],
        lastEventId: action.event.id || state.lastEventId,
      };
    }
    case "step_update":
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...(state.steps[action.stepId] ?? { stepId: action.stepId, status: "pending" as StepStatus, progressPercent: 0 }),
            ...action.patch,
          },
        },
      };
    case "checkpoint_update":
      return { ...state, checkpointPending: action.checkpoint };
    case "error":
      return { ...state, error: action.message };
    case "reset":
      return INITIAL_STATE;
    default:
      return state;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function makeRunEvent(data: Record<string, unknown>): RunEvent {
  return {
    id: typeof data.id === "string" ? data.id : "",
    runId: typeof data.run_id === "string" ? data.run_id : "",
    stepId: typeof data.step_id === "string" ? data.step_id : null,
    eventType: typeof data.event_type === "string" ? data.event_type : "",
    payload: data,
    actorUserId: typeof data.actor_user_id === "string" ? data.actor_user_id : null,
    actorUser: (data.actor_user as RunEvent["actorUser"]) ?? null,
    createdAt: typeof data.created_at === "string" ? data.created_at : new Date().toISOString(),
  };
}

// ── Frame → reducer action dispatcher ────────────────────────────────────────
// Maps "workflow.step.started" → action dispatched to reducer.
// "event" in frame = the action part e.g. "run.started", "step.text_complete".

export function dispatchFrameToReducer(
  frame: Frame,
  dispatchReducer: React.Dispatch<StreamAction>,
): void {
  // Truncated envelopes: REST refetch path owns state update — just log in dev.
  const rawData = frame.data as Record<string, unknown> | null;
  if (rawData?.truncated === true) {
    if (import.meta.env.DEV) {
      console.debug("[useWorkflowRunStream] truncated envelope, awaiting REST refetch", rawData.ref_event_id);
    }
    return;
  }

  const data = rawData ?? {};
  const ev = frame.event; // e.g. "run.started", "step.text_complete"

  // ── Run lifecycle ──────────────────────────────────────────────────────────
  if (ev === "run.queued") {
    dispatchReducer({ type: "run_status", status: "pending" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "run.started") {
    dispatchReducer({ type: "run_status", status: "running" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "run.paused") {
    dispatchReducer({ type: "run_status", status: "paused_checkpoint", paused: true, pausedReason: String(data.reason ?? "") });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "run.resumed") {
    dispatchReducer({ type: "run_status", status: "running", paused: false, pausedReason: undefined });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "run.completed") {
    dispatchReducer({ type: "run_status", status: "success" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    dispatchReducer({ type: "terminated" });
    return;
  }
  if (ev === "run.failed") {
    dispatchReducer({ type: "run_status", status: "failed" });
    dispatchReducer({ type: "error", message: String(data.error ?? "Run failed") });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    dispatchReducer({ type: "terminated" });
    return;
  }
  if (ev === "run.cancelled") {
    dispatchReducer({ type: "run_status", status: "cancelled" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    dispatchReducer({ type: "terminated" });
    return;
  }
  // Maintenance terminal events
  if (ev === "run.cost_aborted" || ev === "run.reaped" || ev === "run.poison_pill") {
    dispatchReducer({ type: "run_status", status: "failed" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    dispatchReducer({ type: "terminated" });
    return;
  }
  if (ev === "run.retried") {
    dispatchReducer({ type: "run_status", status: "pending" });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }

  // ── Step lifecycle ─────────────────────────────────────────────────────────
  if (ev === "step.started") {
    const stepId = String(data.step_id ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_update", stepId, patch: { stepId, status: "running", progressPercent: 0 } });
    dispatchReducer({ type: "event", event: { ...makeRunEvent(data), stepId } });
    return;
  }
  if (ev === "step.completed") {
    const stepId = String(data.step_id ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_update", stepId, patch: {
      status: "success", progressPercent: 100,
      inputCount: data.input_count as number | undefined,
      outputCount: data.output_count as number | undefined,
      durationMs: data.duration_ms as number | undefined,
      costUsd: data.cost_usd as number | undefined,
    }});
    dispatchReducer({ type: "event", event: { ...makeRunEvent(data), stepId } });
    return;
  }
  if (ev === "step.failed") {
    const stepId = String(data.step_id ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_update", stepId, patch: { status: "failed", errorMessage: String(data.error ?? "") } });
    dispatchReducer({ type: "event", event: { ...makeRunEvent(data), stepId } });
    return;
  }
  if (ev === "step.skipped") {
    const stepId = String(data.step_id ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_update", stepId, patch: { status: "skipped" } });
    dispatchReducer({ type: "event", event: { ...makeRunEvent(data), stepId } });
    return;
  }
  if (ev === "step.progress") {
    const stepId = String(data.step_id ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_update", stepId, patch: {
      progressPercent: Number(data.percent ?? data.progress_percent ?? 0),
      progressMessage: String(data.message ?? data.progress_message ?? ""),
    }});
    return;
  }

  // ── Agentic events ─────────────────────────────────────────────────────────
  if (ev === "step.text_complete") {
    const stepId = String(data.step_id ?? "");
    const text = String(data.text ?? data.full_text ?? "");
    if (!stepId) return;
    dispatchReducer({ type: "step_text_complete", stepId, text, event: makeRunEvent(data) });
    return;
  }
  if (ev === "token_delta") {
    const stepId = String(data.step_id ?? "");
    const delta = String(data.delta ?? data.text ?? "");
    if (stepId && delta) dispatchReducer({ type: "token_delta", stepId, delta });
    return;
  }
  if (ev === "agent.decision_made") {
    const stepId = String(data.step_id ?? "");
    const label = String(data.label ?? "");
    const reasoning = String(data.reasoning ?? "");
    const chosenBranchIds = (data.branch_target_ids as string[] | undefined) ?? [];
    if (!stepId) return;
    dispatchReducer({ type: "decision_made", stepId, label, reasoning, chosenBranchIds, event: makeRunEvent(data) });
    return;
  }
  if (ev === "agent.tool_call_start") {
    const stepId = String(data.step_id ?? "");
    if (stepId) dispatchReducer({ type: "step_update", stepId, patch: { status: "tool_running" } });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "agent.tool_call_result") {
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }

  // ── Checkpoint events ──────────────────────────────────────────────────────
  if (ev === "checkpoint.created") {
    dispatchReducer({ type: "checkpoint_update", checkpoint: data as unknown as Checkpoint });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (
    ev === "checkpoint.approved" ||
    ev === "checkpoint.rejected" ||
    ev === "checkpoint.expired" ||
    ev === "checkpoint.cancelled" ||
    ev === "checkpoint.resume_failed"
  ) {
    dispatchReducer({ type: "checkpoint_update", checkpoint: null });
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
  if (ev === "checkpoint.requested") {
    dispatchReducer({ type: "event", event: makeRunEvent(data) });
    return;
  }
}
